import React, { useState } from 'react';
import { Plus, X, Users, Calendar, Lock, Edit2, Trash2, Mail, Download, CheckCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import './AdminPages.css';

const STATUS_BADGE = { Applied:'badge-info', Shortlisted:'badge-gold', 'Interview Scheduled':'badge-warning', Rejected:'badge-danger', Hired:'badge-success' };
const STATUS_FLOW  = ['Applied','Shortlisted','Interview Scheduled','Hired'];

export default function RecruitmentPage() {
  const { data, commitData, showToast } = useApp();
  const { can, isFounder } = useAuth();

  const canManage = can('canManageRecruitment');
  const canPost   = can('canPostJob');
  const canHire   = can('canHireApplicant');

  const [tab, setTab]           = useState('jobs');
  const [showJobModal, setShowJobModal] = useState(false);
  const [showAppModal, setShowAppModal] = useState(false);
  const [editJob, setEditJob]   = useState(null);
  const [viewApp, setViewApp]   = useState(null);
  const [interviewDate, setInterviewDate] = useState('');
  const [jobForm, setJobForm]   = useState({ title:'', dept:'Development', type:'Full-time', deadline:'', description:'' });

  if (!canManage) {
    return (
      <div className="admin-page">
        <div className="page-header"><div><h1 className="page-title">Recruitment</h1></div></div>
        <div className="perm-denied">
          <div className="perm-denied-icon"><Lock size={26} style={{ color:'var(--text-dim)' }}/></div>
          <p style={{ fontWeight:700,fontSize:'1.05rem' }}>Access Restricted</p>
          <p style={{ color:'var(--text-muted)',fontSize:'0.875rem',maxWidth:320,textAlign:'center' }}>Recruitment management is only available to HR Managers and Founders.</p>
        </div>
      </div>
    );
  }

  /* ── Jobs ── */
  const applicantCountForJob = (job) => data.applicants.filter(a => a.job === job.title).length;

  const saveJob = async () => {
    if (!jobForm.title) { showToast('Job title required','error'); return; }
    try {
      if (editJob) {
        await commitData('jobs', data.jobs.map(j=>j.id===editJob.id?{...j,...jobForm}:j));
        showToast('Job posting updated');
      } else {
        await commitData('jobs', [...data.jobs, { id:String(Date.now()),...jobForm,applicants:0,status:'Open' }]);
        showToast('Job posted successfully');
      }
      setShowJobModal(false); setEditJob(null); setJobForm({ title:'',dept:'Development',type:'Full-time',deadline:'',description:'' });
    } catch {}
  };

  const openEditJob = (j) => { setEditJob(j); setJobForm({ title:j.title,dept:j.dept,type:j.type,deadline:j.deadline,description:j.description||'' }); setShowJobModal(true); };

  const deleteJob = async (id) => {
    if (!window.confirm('Delete this job posting?')) return;
    try {
      await commitData('jobs', data.jobs.filter(j=>j.id!==id));
      showToast('Job posting deleted','warning');
    } catch {}
  };

  const toggleJobStatus = async (j) => {
    try {
      await commitData('jobs', data.jobs.map(job=>job.id===j.id?{...job,status:job.status==='Open'?'Closed':'Open'}:job));
      showToast(`Job ${j.status==='Open'?'closed':'reopened'}`);
    } catch {}
  };

  /* ── Applicants ── */
  const moveStatus = async (id, status) => {
    try {
      await commitData('applicants', data.applicants.map(a=>a.id===id?{...a,status}:a));
      showToast(`Applicant moved to ${status}`);
    } catch {}
  };

  const scheduleInterview = async () => {
    if (!interviewDate) { showToast('Select interview date','error'); return; }
    try {
      await commitData('applicants', data.applicants.map(a=>a.id===viewApp.id?{...a,status:'Interview Scheduled',interviewDate}:a));
      showToast('Interview scheduled — applicant will receive an email if mail is configured');
      setShowAppModal(false); setViewApp(null);
    } catch {}
  };

  const hireApplicant = async (app) => {
    try {
      await commitData('applicants', data.applicants.map(a=>a.id===app.id?{...a,status:'Hired'}:a));
      showToast(`${app.name} hired — applicant and HR notified by email when mail is configured`);
    } catch {}
  };

  const rejectApplicant = async (id) => {
    try {
      await commitData('applicants', data.applicants.map(a=>a.id===id?{...a,status:'Rejected'}:a));
      showToast('Applicant rejected — rejection email sent when mail is configured','warning');
    } catch {}
  };

  const newApps = data.applicants.filter(a=>a.status==='Applied').length;

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Recruitment</h1>
          <p className="page-subtitle">{data.jobs.filter(j=>j.status==='Open').length} open positions · {data.applicants.length} applicants</p>
        </div>
        <div className="page-actions">
          {canPost && <button className="btn-gold" onClick={()=>{setEditJob(null);setJobForm({title:'',dept:'Development',type:'Full-time',deadline:'',description:''});setShowJobModal(true)}}><Plus size={15}/> Post Job</button>}
        </div>
      </div>

      <div className="info-banner info-banner-green" style={{ marginBottom:20 }}>
        <Lock size={14}/><span>Recruitment is restricted to <strong>HR Managers and Founders</strong> only.</span>
      </div>

      <div className="filter-tabs" style={{ marginBottom:20,width:'fit-content' }}>
        <button className={`filter-tab ${tab==='jobs'?'active':''}`} onClick={()=>setTab('jobs')}>Job Postings</button>
        <button className={`filter-tab ${tab==='pipeline'?'active':''}`} onClick={()=>setTab('pipeline')}>
          Applicants {newApps>0&&<span className="badge badge-info" style={{ marginLeft:4 }}>{newApps}</span>}
        </button>
      </div>

      {/* ── JOB POSTINGS ── */}
      {tab==='jobs' && (
        <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
          {data.jobs.map(job=>(
            <div key={job.id} style={{ background:'var(--surface-color)',border:'1px solid var(--border-color)',borderRadius:'var(--radius-lg)',padding:'18px 20px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:16,transition:'var(--transition)' }}>
              <div style={{ display:'flex',alignItems:'center',gap:14 }}>
                <div className="stat-icon"><Users size={18}/></div>
                <div>
                  <p style={{ fontWeight:700,fontSize:'0.95rem' }}>{job.title}</p>
                  <div style={{ display:'flex',gap:12,fontSize:'0.78rem',color:'var(--text-muted)',marginTop:3,flexWrap:'wrap' }}>
                    <span>{job.dept}</span><span>·</span><span>{job.type}</span>
                    {job.deadline&&<><span>·</span><span>Deadline: {new Date(job.deadline).toLocaleDateString('en-ZA')}</span></>}
                    <span>·</span><span>{applicantCountForJob(job)} applicants</span>
                  </div>
                </div>
              </div>
              <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                <span className={`badge ${job.status==='Open'?'badge-success':'badge-gray'}`}>{job.status}</span>
                <button className="btn-ghost" style={{ padding:'6px 12px',fontSize:'0.75rem' }} onClick={()=>toggleJobStatus(job)}>
                  {job.status==='Open'?'Close':'Reopen'}
                </button>
                <button className="btn-ghost" style={{ padding:'6px' }} onClick={()=>openEditJob(job)}><Edit2 size={13}/></button>
                {isFounder&&<button className="btn-ghost" style={{ padding:'6px',color:'var(--danger)' }} onClick={()=>deleteJob(job.id)}><Trash2 size={13}/></button>}
              </div>
            </div>
          ))}
          {data.jobs.length===0&&<p style={{ color:'var(--text-dim)',fontSize:'0.875rem' }}>No job postings yet.</p>}
        </div>
      )}

      {/* ── APPLICANT PIPELINE ── */}
      {tab==='pipeline' && (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Applicant</th><th>Position</th><th>Applied</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {data.applicants.map(a=>(
                <tr key={a.id}>
                  <td>
                    <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                      <div className="av av-sm av-gold">{a.name.split(' ').map(n=>n[0]).join('').slice(0,2)}</div>
                      <div>
                        <p style={{ fontWeight:600,fontSize:'0.875rem' }}>{a.name}</p>
                        <p style={{ fontSize:'0.73rem',color:'var(--text-muted)' }}>{a.email}</p>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize:'0.875rem' }}>{a.job}</td>
                  <td style={{ fontSize:'0.8rem',color:'var(--text-muted)' }}>{new Date(a.date).toLocaleDateString('en-ZA')}</td>
                  <td><span className={`badge ${STATUS_BADGE[a.status]||'badge-gray'}`}>{a.status}</span></td>
                  <td>
                    <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                      <button className="btn-ghost" style={{ padding:'5px 8px',fontSize:'0.7rem' }} onClick={()=>{setViewApp(a);setInterviewDate('');setShowAppModal(true)}}><Mail size={11}/> View</button>
                      {a.status==='Applied'     && <button className="btn-outline" style={{ padding:'5px 10px',fontSize:'0.7rem' }} onClick={()=>moveStatus(a.id,'Shortlisted')}>Shortlist</button>}
                      {a.status==='Shortlisted' && <button className="btn-outline" style={{ padding:'5px 10px',fontSize:'0.7rem' }} onClick={()=>{setViewApp(a);setShowAppModal(true)}}>Schedule Interview</button>}
                      {a.status==='Interview Scheduled' && canHire && <button className="btn-gold" style={{ padding:'5px 10px',fontSize:'0.7rem' }} onClick={()=>hireApplicant(a)}><CheckCircle size={11}/> Hire</button>}
                      {a.status!=='Rejected'&&a.status!=='Hired' && <button className="btn-ghost" style={{ padding:'5px 8px',fontSize:'0.7rem',color:'var(--danger)' }} onClick={()=>rejectApplicant(a.id)}>Reject</button>}
                      {a.cvUrl ? <a href={a.cvUrl} target="_blank" rel="noreferrer" className="btn-ghost" style={{ padding:'5px 8px',fontSize:'0.7rem',display:'inline-flex',alignItems:'center',gap:4 }}><Download size={11}/> CV</a> : <span className="btn-ghost" style={{ padding:'5px 8px',fontSize:'0.7rem',opacity:0.5 }}>No CV</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── VIEW APPLICANT MODAL ── */}
      {showAppModal && viewApp && (
        <div className="modal-overlay" onClick={()=>setShowAppModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><p className="modal-title">{viewApp.name}</p><p className="modal-subtitle">{viewApp.job}</p></div>
              <button className="modal-close" onClick={()=>setShowAppModal(false)}><X size={18}/></button>
            </div>
            <div className="detail-grid">
              <div className="detail-item"><span className="detail-label">Email</span><span className="detail-value" style={{ fontSize:'0.8rem' }}>{viewApp.email}</span></div>
              <div className="detail-item"><span className="detail-label">Phone</span><span className="detail-value">{viewApp.phone||'—'}</span></div>
              <div className="detail-item"><span className="detail-label">Applied</span><span className="detail-value">{new Date(viewApp.date).toLocaleDateString('en-ZA')}</span></div>
              <div className="detail-item"><span className="detail-label">Status</span><span className={`badge ${STATUS_BADGE[viewApp.status]||'badge-gray'}`}>{viewApp.status}</span></div>
            </div>
            {viewApp.notes && <div className="info-banner info-banner-gold" style={{ marginBottom:16 }}><span>{viewApp.notes}</span></div>}
            {viewApp.interviewDate && <div className="info-banner info-banner-blue"><Calendar size={14}/><span>Interview: {new Date(viewApp.interviewDate).toLocaleDateString('en-ZA')}</span></div>}

            {/* Schedule interview section */}
            {(viewApp.status==='Applied'||viewApp.status==='Shortlisted') && (
              <div style={{ marginBottom:16 }}>
                <p className="form-label" style={{ marginBottom:8,display:'block' }}>Schedule Interview Date</p>
                <div style={{ display:'flex',gap:10 }}>
                  <input className="form-input" type="date" value={interviewDate} onChange={e=>setInterviewDate(e.target.value)} style={{ flex:1 }}/>
                  <button className="btn-gold" onClick={scheduleInterview} style={{ padding:'9px 16px',fontSize:'0.8rem' }}>Schedule & Notify</button>
                </div>
              </div>
            )}

            <div style={{ display:'flex',gap:10,justifyContent:'flex-end',marginTop:8 }}>
              {viewApp.cvUrl ? <a href={viewApp.cvUrl} target="_blank" rel="noreferrer" className="btn-ghost" style={{ display:'flex',alignItems:'center',gap:6,fontSize:'0.8rem' }}><Download size={13}/> Download CV</a> : null}
              {viewApp.status==='Interview Scheduled' && canHire && (
                <button className="btn-gold" onClick={()=>{hireApplicant(viewApp);setShowAppModal(false)}}><CheckCircle size={14}/> Hire Applicant</button>
              )}
              {viewApp.status!=='Rejected'&&viewApp.status!=='Hired' && (
                <button className="btn-ghost" style={{ color:'var(--danger)' }} onClick={()=>{rejectApplicant(viewApp.id);setShowAppModal(false)}}>Reject</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── JOB POSTING MODAL ── */}
      {showJobModal && (
        <div className="modal-overlay" onClick={()=>setShowJobModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <p className="modal-title">{editJob?'Edit Job Posting':'Post New Job'}</p>
              <button className="modal-close" onClick={()=>setShowJobModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-form">
              <div className="form-group"><label className="form-label">Job Title *</label><input className="form-input" value={jobForm.title} onChange={e=>setJobForm({...jobForm,title:e.target.value})} placeholder="E.g. Senior React Developer"/></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Department</label>
                  <select className="form-input" value={jobForm.dept} onChange={e=>setJobForm({...jobForm,dept:e.target.value})}>
                    {['Development','Design','Systems','HR','Finance','Operations'].map(d=><option key={d}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Type</label>
                  <select className="form-input" value={jobForm.type} onChange={e=>setJobForm({...jobForm,type:e.target.value})}>
                    {['Full-time','Part-time','Contract','Internship'].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group"><label className="form-label">Application Deadline</label><input className="form-input" type="date" value={jobForm.deadline} onChange={e=>setJobForm({...jobForm,deadline:e.target.value})}/></div>
              <div className="form-group"><label className="form-label">Job Description</label><textarea className="form-input" rows={4} value={jobForm.description} onChange={e=>setJobForm({...jobForm,description:e.target.value})} placeholder="Role overview, requirements, responsibilities..."/></div>
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={()=>setShowJobModal(false)}>Cancel</button>
              <button className="btn-gold" onClick={saveJob}>{editJob?'Save Changes':'Post Job'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
